import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import {
  Cell,
  Empty,
  FieldWrapper,
  Filter,
  FilterButton,
  HeaderCell,
  HeaderRow,
  Input,
  PaginationFooter,
  PaymentOverviewStats,
  PaymentStatusPills,
  Row,
  Section,
  SectionHeader,
  Select,
  SortLabel,
  Table,
  TableBody,
  TableHead,
  Text,
} from 'components';
import ButtonExportPayrollTipsCSVAsync from 'components/Button/ButtonExportPayrollTipsCsvAsync';
import { theme, toRem } from 'design';
import ListContext, { ListContextProvider } from 'providers/ListContextProvider';
import { URL } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { EmployeePayrollTips, getEmployeePayrollTipsRequest } from 'store/Collections';
import { selectEmployeePayrollTips, selectEmployeePayrollTipsTotal } from 'store/Collections/EmployeePayrollTips/selectors';
import { getPayPeriodsRequest, selectPayPeriods } from 'store/Collections/PayPeriods';
import { selectSite } from 'store/Site';
import { getSitePayoutStatsRequest, selectPayoutStats, Stats } from 'store/Stats';
import { IStore } from 'store/types';
import { ApiQueryOperator, DisbursementMethod, IApiQueryParam, IEmployeePayrollTip, IPayPeriod, PayoutStatus } from 'types';
import { getFirstElement, getLastElement } from 'utils/arrays';
import { centsToDollarsDisplay, centsToNegativeDollarsDisplay } from 'utils/currency';
import { formatDateForApi, formatDateForUI } from 'utils/date';
import { PayrollPeriodWrapper, PayrollTipsRow } from './components';

const StyledSectionHeader = styled(SectionHeader)`
  width: 100%;

  @media (min-width: ${theme.breakpoint.med}px) {
    flex-flow: row wrap;
  }

  section {
    width: 100%;

    @media (min-width: ${theme.breakpoint.med}px) {
      width: auto;
      margin-left: 0;
      flex-flow: row wrap;
    }
  }
`;

const PayrollPeriodSection = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  width: 100%;
  gap: ${toRem(8)};
`;

const PayrollPaymentLabel = styled(Text).attrs({
  as: 'label',
})`
  font-size: ${toRem(11)};
  font-weight: ${theme.font.weight.medium};
  display: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    font-size: ${toRem(13.25)};
    white-space: nowrap;
    display: block;
  }
`;

const DEFAULT_SORT = 'employee_name';
const DEFAULT_STATUS_FILTERS = [PayoutStatus.PAID, PayoutStatus.PENDING, PayoutStatus.PAYROLL, PayoutStatus.EXPIRED];

const ReportsPayrollTips = () => {
  // UI State
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const currentSite = useSelector(selectSite());
  const { organizationId: currentOrganizationId, siteId: currentSiteId } = useSelector(selectCurrentEmployee()) || {};
  const tips = useSelector(selectEmployeePayrollTips());
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, EmployeePayrollTips.GET_EMPLOYEE_PAYROLL_TIPS_REQUEST));
  const payoutStats = useSelector(selectPayoutStats());
  const isLoadingPayoutStats = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_SITE_PAYOUT_STATS_REQUEST));

  const fetchData = useCallback(
    (queryParams: IApiQueryOptions) => {
      const startDate = (queryParams.q || []).find((q) => q.key === 'payment_date' && q.operator === ApiQueryOperator.gte)?.value;
      const endDate = (queryParams.q || []).find((q) => q.key === 'payment_date' && q.operator === ApiQueryOperator.lte)?.value;

      // remove payment_date from query params
      const paramsWithoutPaymentDate = { ...queryParams, q: queryParams.q?.filter((q) => q.key !== 'payment_date') };

      if (currentSiteId && startDate && endDate) {
        (() => {
          dispatch(getEmployeePayrollTipsRequest(currentSiteId, startDate.toString(), endDate.toString(), paramsWithoutPaymentDate));
          dispatch(getSitePayoutStatsRequest(currentSiteId, startDate.toString(), endDate.toString(), paramsWithoutPaymentDate));
        })();
      }
    },
    [currentSiteId]
  );

  const paymentPeriods = useSelector(selectPayPeriods({ orgId: currentOrganizationId, siteId: currentSiteId }));
  const reverseChronologicalPaymentPeriods = paymentPeriods && reverse(sortBy(paymentPeriods, ({ start }) => dayjs(start, 'YYYY-MM-DD', true).valueOf()));

  const latestPaymentPeriod = getLastElement(paymentPeriods);
  const DEFAULT_END_DATE = latestPaymentPeriod?.end;
  const DEFAULT_START_DATE = latestPaymentPeriod?.start;

  const paymentPeriodOptions = reverseChronologicalPaymentPeriods?.map((period) => ({
    label: `${formatDateForUI(period.start)} - ${formatDateForUI(period.end)}`,
    value: period,
  }));
  const initialSelectedPeriodOption = getFirstElement(paymentPeriodOptions);

  useEffect(() => {
    if (currentOrganizationId && currentSiteId && !paymentPeriods) dispatch(getPayPeriodsRequest({ orgId: currentOrganizationId, siteId: currentSiteId }));
  }, [currentOrganizationId, currentSiteId, paymentPeriods, dispatch]);

  return (
    <>
      {!!currentOrganizationId && !!currentSiteId && DEFAULT_START_DATE && DEFAULT_END_DATE && (
        <ListContextProvider
          fetchData={fetchData}
          totalSelector={selectEmployeePayrollTipsTotal()}
          defaultOffset={0}
          defaultLimit={10}
          defaultSort={DEFAULT_SORT}
          defaultFilters={[
            {
              key: 'payment_date',
              operator: ApiQueryOperator.gte,
              value: formatDateForApi(DEFAULT_START_DATE),
            },
            {
              key: 'payment_date',
              operator: ApiQueryOperator.lte,
              value: formatDateForApi(DEFAULT_END_DATE),
            },
            {
              key: 'status',
              operator: ApiQueryOperator.in,
              value: DEFAULT_STATUS_FILTERS,
            },
          ]}
          dataKey="reportsPayrollTips"
        >
          <ListContext.Consumer>
            {({ methods, components, query }) => {
              const payeesTotal = tips?.total || 0;
              const statuses: string[] = (query.q || []).find((q: IApiQueryParam) => q.key === 'status' && q.operator === ApiQueryOperator.in)?.value || [];
              const { resultsPerPage: ResultsPerPage, pagination: Pager } = components;
              const { onFiltersChange, onSortChange, isSortedBy, isSortDesc } = methods;
              const { q } = query;

              const isDescendingSort = isSortDesc();
              const [nameSorted, grossSorted, holdbackSorted, netSorted, instantSorted, payrollSorted] = ['employee_name', 'gross', 'holdback', 'net', 'instant', 'payroll'].map((header) => isSortedBy(header));
              const debounceMs = 500;
              const onNameChange = (newName: string) => {
                const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'employee_name') as IApiQueryParam[];
                const filters =
                  newName.trim() !== ''
                    ? [
                        ...otherFilters,
                        {
                          key: 'employee_name',
                          operator: ApiQueryOperator.like,
                          value: newName,
                        },
                      ]
                    : otherFilters;
                onFiltersChange(filters);
              };
              const onStatusChange = (newStatus: string[]) => {
                const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'status') as IApiQueryParam[];
                const newStatusFilters =
                  newStatus?.length > 0
                    ? [
                        {
                          key: 'status',
                          operator: ApiQueryOperator.in,
                          value: newStatus,
                        },
                      ]
                    : [
                        {
                          key: 'status',
                          operator: ApiQueryOperator.nin,
                          value: Object.values(PayoutStatus),
                        },
                      ];

                const filters = [...otherFilters, ...newStatusFilters];
                onFiltersChange(filters, true);
              };
              const onEmployeeIdChange = (newId: string) => {
                const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'employee.sites.employee_id') as IApiQueryParam[];
                const filters =
                  newId.trim() !== ''
                    ? [
                        ...otherFilters,
                        {
                          key: 'employee.sites.employee_id',
                          operator: ApiQueryOperator.like,
                          value: newId,
                        },
                      ]
                    : otherFilters;
                onFiltersChange(filters);
              };
              const onDateRangeChange = (dateRange: IPayPeriod) => {
                const otherFilters = Object.values(q || []).filter((query: any) => query.key !== 'payment_date') as IApiQueryParam[];
                const filters = [
                  ...otherFilters,
                  {
                    key: 'payment_date',
                    operator: ApiQueryOperator.gte,
                    value: dateRange.start,
                  },
                  {
                    key: 'payment_date',
                    operator: ApiQueryOperator.lte,
                    value: dateRange.end,
                  },
                ];
                onFiltersChange(filters);
              };

              const debouncedOnNameChange = debounce(onNameChange, debounceMs);
              const debouncedOnEmployeeIdChange = debounce(onEmployeeIdChange, debounceMs);

              const selectedPayPeriodStart = (query.q || []).find((q: IApiQueryParam) => q.key === 'payment_date' && q.operator === ApiQueryOperator.gte)?.value;
              const selectedPayPeriodEnd = (query.q || []).find((q: IApiQueryParam) => q.key === 'payment_date' && q.operator === ApiQueryOperator.lte)?.value;
              const getDisplayFilters = (q: IApiQueryParam[] = []) => q.filter((item) => !['payment_date', 'status'].includes(item.key));

              return (
                <>
                  <Section data-cy="table">
                    <StyledSectionHeader isLoading={isLoading} title="Payees" value={`(${payeesTotal})`} zIndex={250}>
                      <PayrollPeriodSection>
                        <PayrollPaymentLabel>Payroll Period</PayrollPaymentLabel>
                        <PayrollPeriodWrapper>
                          <Select
                            data-cy="periods-select"
                            placeholder="Select payroll period"
                            options={paymentPeriodOptions || []}
                            values={[initialSelectedPeriodOption]}
                            onChange={(selectedOptions) => {
                              if (selectedOptions?.[0]) onDateRangeChange(selectedOptions[0].value);
                            }}
                          />
                        </PayrollPeriodWrapper>
                        <FilterButton filterActive={filterActive} appliedFilters={getDisplayFilters(q).length} onClick={() => setFilterActive(!filterActive)} />
                        <ButtonExportPayrollTipsCSVAsync disabled={!tips || tips.total === 0 || isLoading} query={query} site={currentSite} />
                      </PayrollPeriodSection>
                    </StyledSectionHeader>

                    <Filter isVisible={filterActive}>
                      <FieldWrapper label="Name" data-cy="filter-name">
                        <Input placeholder="Name" onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnNameChange(e.currentTarget.value)} />
                      </FieldWrapper>

                      <FieldWrapper label="Employee ID" data-cy="filter-employee">
                        <Input placeholder="Employee ID" onChange={(e: React.FormEvent<HTMLInputElement>) => debouncedOnEmployeeIdChange(e.currentTarget.value)} />
                      </FieldWrapper>
                    </Filter>

                    <PaymentStatusPills isLoading={isLoadingPayoutStats} payoutStats={payoutStats} activeStatuses={statuses} onStatusChange={(s: string[]) => onStatusChange(s)} />
                    <PaymentOverviewStats isLoading={isLoadingPayoutStats} payoutStats={payoutStats} />

                    <Table sticky scrollable>
                      <TableHead>
                        <HeaderRow sticky>
                          <HeaderCell>
                            <SortLabel label="Name" data-cy="employee-name-header" isSorted={nameSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('employee_name')} isLoading={isLoading} />
                          </HeaderCell>
                          <HeaderCell>{!isLoading && 'Status'}</HeaderCell>
                          <HeaderCell align="left">{!isLoading && 'Tips'}</HeaderCell>
                          <HeaderCell align="left">{!isLoading && 'Autograts'}</HeaderCell>
                          <HeaderCell align="left">
                            <SortLabel label="Gross" data-cy="gross-header" isSorted={grossSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('gross')} isLoading={isLoading} />
                          </HeaderCell>
                          <HeaderCell align="left">
                            <SortLabel label="Holdback" data-cy="holdback-header" isSorted={holdbackSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('holdback')} isLoading={isLoading} />
                          </HeaderCell>
                          <HeaderCell align="left">
                            <SortLabel label="Instant" data-cy="instant-header" isSorted={instantSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange(DisbursementMethod.INSTANT)} isLoading={isLoading} />
                          </HeaderCell>
                          <HeaderCell align="left">
                            <SortLabel label="Payroll" data-cy="payroll-header" isSorted={payrollSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange(DisbursementMethod.PAYROLL)} isLoading={isLoading} />
                          </HeaderCell>
                          <HeaderCell align="left">
                            <SortLabel label="Net" data-cy="net-header" isSorted={netSorted} isSortedDesc={isDescendingSort} onClick={() => onSortChange('net')} isLoading={isLoading} />
                          </HeaderCell>
                        </HeaderRow>
                      </TableHead>
                      <TableBody>
                        {tips?.data.length === 0 ? (
                          <Row>
                            <Cell colSpan={9}>
                              <Empty container containerHeight={300} icon="Payment" message="No results for selected payroll period." />
                            </Cell>
                          </Row>
                        ) : (
                          tips?.data.map((datum: IEmployeePayrollTip, index: number) => {
                            const siteEmployeeId = (datum.employee.sites || []).find((site: any) => site.id === currentSiteId)?.employeeId || '--';

                            return (
                              <PayrollTipsRow
                                key={`${index}-${datum.employee.id}`}
                                data-cy="payroll-tips-row"
                                data-value={datum.employee.id}
                                employeeId={siteEmployeeId}
                                tips={centsToDollarsDisplay(datum.instant.tips + datum.payroll.tips)}
                                autoGratuity={centsToDollarsDisplay(datum.payroll.autoGratuities + datum.instant.autoGratuities)}
                                gross={centsToDollarsDisplay(datum.gross)}
                                holdback={centsToNegativeDollarsDisplay(datum.holdback)}
                                instant={centsToDollarsDisplay(datum.instant.total - datum.holdback)}
                                payroll={centsToDollarsDisplay(datum.payroll.total)}
                                total={centsToDollarsDisplay(datum.net)}
                                isLoading={isLoading}
                                userId={datum.employee?.user?.id}
                                userHasPaymentMethod={!!datum.employee.user?.card}
                                userImage={datum.employee.user?.avatarUrl}
                                userName={datum.employee.name}
                                statusCounts={datum.statusCounts}
                                onClick={() => {
                                  history.push({
                                    pathname: `${URL.ReportsEmployeePayPeriod}/${datum.employee.id}/${selectedPayPeriodStart}/${selectedPayPeriodEnd}`,
                                  });
                                }}
                              />
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                    <PaginationFooter>
                      {!isLoading && !!tips && tips.total > query.limit && ResultsPerPage}
                      {!isLoading && !!tips && tips.total > query.limit && Pager}
                    </PaginationFooter>
                  </Section>
                </>
              );
            }}
          </ListContext.Consumer>
        </ListContextProvider>
      )}
    </>
  );
};

export default ReportsPayrollTips;
